import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useRef
} from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { withStyles } from '@material-ui/styles';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  Typography,
  TextField,
  IconButton,
  Box,
  Button,
  Modal,
  FormControl,
  MenuItem,
  Select,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TableBody,
  Grid,
  InputAdornment,
} from '@material-ui/core';
import {
  FirstPage,
  CloseOutlined,
  DescriptionOutlined,
  EditOutlined,
  DeleteOutlined,
  CalendarToday,
  SaveOutlined,
  BorderColorOutlined,
  ScienceOutlined,
  ImageSearchOutlined,
  LocalHospitalOutlined,
  MedicalServicesOutlined,
} from '@material-ui/icons';
import styles from './../../../style';
import { VSHx, ApRx, ApLabOrders, MyTemplates, CPT, SoapPast } from './';
import {
  downloadPDFRx,
  downloadPDFLab,
  downloadPDFImage,
  downloadPDFPhysician,
  saveSOAPPatientData,
  savePrescription,
  savelabOrders,
  saveImagingOrders,
  savePhysicianOrders,
  saveDiagnosis,
  deleteSoap,
  deleteDiagnosis,
  getPastSoapNoteById,
  resetValue
} from 'services/soap/action';
import { connect } from 'react-redux';
import MessageBox from 'util/messageBox';
import schema from './schemaEntry';
import validate from 'validate.js';
import { Roles } from 'util/enum';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { saveTempData } from 'services/soap/action';

// ─── Inline button styles ────────────────────────────────────────────────────

const btnBase = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  borderRadius: 8,
  fontSize: 12,
  fontWeight: 400,
  letterSpacing: '0.04em',
  cursor: 'pointer',
  transition: 'all 0.18s ease',
  border: 'none',
  padding: '6px 14px',
  lineHeight: 1.4,
  fontFamily: 'inherit',
};

const btnStyles = {
  // Primary action: Save
  save: {
    ...btnBase,
    background: 'linear-gradient(135deg, #197cd7 0%, #197cd7 100%)',
    color: '#fff',
    fontSize: 12,
    padding: '6px 10px',
  },
  // Secondary action: Save & E-Sign
  sign: {
    ...btnBase,
    background: 'linear-gradient(135deg, #DDAC5D 0%, #DDAC5D 100%)',
    color: '#fff',
    fontSize: 12,
    padding: '6px 10px',
  },
  // Destructive: Delete
  danger: {
    ...btnBase,
    background: 'linear-gradient(135deg, #e53935 0%, #b71c1c 100%)',
    color: '#fff',
    boxShadow: '0 2px 6px rgba(229,57,53,0.28)',
  },
  // Ghost/outlined: VS Hx
  ghost: {
    ...btnBase,
    background: 'transparent',
    color: '#1a73e8',
    border: '1.5px solid #1a73e8',
    boxShadow: 'none',
  },
  // Order buttons (tonal)
  rx: {
    ...btnBase,
    background: '#e8f0fe',
    color: '#1a73e8',
    border: '1.5px solid #c5d8fc',
  },
  lab: {
    ...btnBase,
    background: '#e6f4ea',
    color: '#137333',
    border: '1.5px solid #b7dfc5',
  },
  imaging: {
    ...btnBase,
    background: '#fef3e2',
    color: '#b05c00',
    border: '1.5px solid #f5cfa0',
  },
  physician: {
    ...btnBase,
    background: '#fce8e6',
    color: '#b3261e',
    border: '1.5px solid #f5c2bf',
  },
  // Template icon button
  template: {
    ...btnBase,
    background: '#f1f3f4',
    color: '#444',
    border: '1.5px solid #dadce0',
    padding: '5px 10px',
    borderRadius: 8,
  },
  // Cancel
  cancel: {
    ...btnBase,
    background: '#f1f3f4',
    color: '#444',
    border: '1.5px solid #dadce0',
  },
  // CPT
  cpt: {
    ...btnBase,
    background: '#ede7f6',
    color: '#4527a0',
    border: '1.5px solid #c5b5e8',
  },
  // ICD
  icd: {
    ...btnBase,
    background: '#e3f2fd',
    color: '#0d47a1',
    border: '1.5px solid #90caf9',
  },
  disabled: {
    opacity: 0.45,
    cursor: 'not-allowed',
  },
};

// Simple styled button wrapper
const Btn = ({ variant = 'save', disabled, onClick, children, style }) => (
  <button
    disabled={disabled}
    onClick={disabled ? undefined : onClick}
    style={{
      ...btnStyles[variant],
      ...(disabled ? btnStyles.disabled : {}),
      ...style,
    }}
  >
    {children}
  </button>
);

// ─────────────────────────────────────────────────────────────────────────────

const SoapEntry = forwardRef((props, ref) => {
  useImperativeHandle(ref, () => {
    return {
      getValuesforCopy,
      saveTempData,
      loadCopyEditSoapDetails
    };
  });

  const {
    classes,
    soap,
    ehrID,
    userID,
    patientId,
    patientInfo,
    providers,
    role,
    user,
    getPastSoapNoteById,
    resetValue
  } = props;

  const [pros, setPros] = useState([]);
  const [succMsg, setSuccMsg] = useState(false);
  let [editSoap, setEditSoap] = useState([]);
  let [isCPT, setIsCPT] = useState(false);
  const [isCopy, setIsCopy] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [isPast, setIsPast] = useState(false);
  const [opendelete, setOpenDelete] = useState(false);
  const [succDeleteMsg, setSuccDeleteMsg] = useState(false);

  const subjectiveRef = useRef(null);
  const objectiveRef = useRef(null);
  const assessmentRef = useRef(null);

  const initialUserState = {
    id: 0,
    patientId: patientId,
    bloodpressure: '',
    temperature: '',
    heartrate: '',
    respiratoryrate: '',
    weight: '',
    bodymassindex: '',
    height: '',
    subjective: '',
    objective: '',
    assessmentplan: '',
    diagnosis: '',
    visitdate: new Date(),
    providerId: userID,
    signed: false,
    isValid: false,
    cptList: [],
    addendum: '',
    diagnosisList: [],
    medicationComment: '',
    touched: {},
    errors: {}
  };

  const [values, setValues] = useState(initialUserState);

  useEffect(() => {
    if (providers != undefined) {
      let providertopush = [];
      providers.forEach(function (item) {
        providertopush.push({
          value: item.id,
          key:
            (item.firstname ? item.firstname : '') +
            (item.lastname ? ' ' + item.lastname : '') +
            (item.title ? ' ' + item.title : '')
        });
      });
      setPros(providertopush);
    }
  }, [providers]);

  const loadCopyEditSoapDetails = (
    soap,
    isEdit = false,
    isCopy = false,
    isPast = false
  ) => {
    setIsPast(isPast);
    setIsCopy(isCopy);
    setIsEdit(isEdit);
    if (soap && soap.soap) {
      if (isEdit || isCopy) {
        if (isEdit) {
          if (soap.editSoap) {
            setEditSoap(soap.editSoap);
          } else {
            setEditSoap(soap.soap);
          }
        }
        let newState = { ...values };
        newState.patientId = soap.soap && soap.soap.patient && soap.soap.patient.id;
        newState.isCopyNeglect = soap.isCopyNeglect;
        if (!isCopy) {
          if (soap.soap && soap.soap.soapnote) {
            newState.bloodpressure = soap.soap.soapnote.bloodpressure;
            newState.temperature = soap.soap.soapnote.temperature;
            newState.heartrate = soap.soap.soapnote.heartrate;
            newState.respiratoryrate = soap.soap.soapnote.respiratoryrate;
            newState.weight = soap.soap.soapnote.weight;
            newState.bodymassindex = soap.soap.soapnote.bodymassindex;
            newState.height = soap.soap.soapnote.height;
          } else {
            newState.bloodpressure = soap.soap.bloodpressure;
            newState.temperature = soap.soap.temperature;
            newState.heartrate = soap.soap.heartrate;
            newState.respiratoryrate = soap.soap.respiratoryrate;
            newState.weight = soap.soap.weight;
            newState.bodymassindex = soap.soap.bodymassindex;
            newState.height = soap.soap.height;
          }
        } else if (soap.isCopyNeglect) {
          newState.bloodpressure = soap.soap.bloodpressure;
          newState.temperature = soap.soap.temperature;
          newState.heartrate = soap.soap.heartrate;
          newState.respiratoryrate = soap.soap.respiratoryrate;
          newState.weight = soap.soap.weight;
          newState.bodymassindex = soap.soap.bodymassindex;
          newState.height = soap.soap.height;
        }
        if (soap.soap && soap.soap.soapnote) {
          newState.id = isEdit ? soap.soap.soapnote.id : 0;
          newState.subjective = soap.soap.soapnote.subjective;
          newState.objective = soap.soap.soapnote.objective;
          newState.assessmentplan = soap.soap.soapnote.assessmentplan;
          newState.diagnosis = soap.soap.soapnote.diagnosis;
          newState.providerId = soap.soap.provider.id ? soap.soap.provider.id : userID;
          newState.signed = isEdit ? soap.soap.soapnote.signed : false;
          newState.visitdate = isEdit ? soap.soap.visitdate : new Date();
          newState.medicationComment = isEdit ? soap.soap.soapnote.medicationComment : '';
          if (soap.soap && soap.soap.soapnote && soap.soap.soapnote.medicationComment && isEdit) {
            setMedicationComment(soap.soap.soapnote.medicationComment);
          }
        } else {
          newState.id = isEdit ? soap.soap.id : 0;
          newState.subjective = soap.soap.subjective;
          newState.objective = soap.soap.objective;
          newState.assessmentplan = soap.soap.assessmentplan;
          newState.diagnosis = soap.soap.diagnosis;
          newState.providerId = soap.soap.providerId;
          newState.visitdate = soap.soap.visitdate;
          newState.medicationComment = soap && soap.soap && soap.soap.medicationComment;
          if (soap.soap && soap.soap.medicationComment) {
            setMedicationComment(soap.soap.medicationComment);
          }
        }
        if (isEdit) {
          if (soap?.pastSoapNoteDetails?.diagnosisList?.length > 0) {
            newState.diagnosisList = soap.pastSoapNoteDetails.diagnosisList;
          }
          if (soap?.pastSoapNoteDetails?.cptList?.length > 0) {
            newState.cptList = soap.pastSoapNoteDetails.cptList;
          }
        } else {
          if (soap?.pastSoapNoteDetails?.diagnosisList?.length > 0) {
            newState.diagnosisList = soap.pastSoapNoteDetails.diagnosisList.map(item => ({
              code: item.code,
              description: item.description,
              codeType: item.codeType
            }));
          }
          if (soap?.pastSoapNoteDetails?.cptList?.length > 0) {
            newState.cptList = soap.pastSoapNoteDetails.cptList.map(item => ({
              code: item.code,
              description: item.description,
              codeType: item.codeType
            }));
          }
        }
        setValues(newState);
      } else if (soap && soap.soap && !isEdit && !isCopy) {
        let newState = { ...values };
        let soapData = soap.soap;
        newState.patientId = soapData.patientId;
        newState.bloodpressure = soapData.bloodpressure;
        newState.temperature = soapData.temperature;
        newState.heartrate = soapData.heartrate;
        newState.respiratoryrate = soapData.respiratoryrate;
        newState.weight = soapData.weight;
        newState.bodymassindex = soapData.bodymassindex;
        newState.height = soapData.height;
        newState.subjective = soapData.subjective;
        newState.objective = soapData.objective;
        newState.assessmentplan = soapData.assessmentplan;
        newState.diagnosis = soapData.diagnosis;
        newState.providerId = soapData.providerId;
        newState.visitdate = soapData.visitdate;
        newState.medicationComment = soapData && soapData.medicationComment;
        if (soap.soap && soap.soap.medicationComment) {
          setMedicationComment(soap.soap.medicationComment);
        }
        if (soap.pastSoapNoteDetails?.diagnosisList?.length > 0) {
          newState.diagnosisList = soap.pastSoapNoteDetails.diagnosisList;
        }
        if (soap.pastSoapNoteDetails?.cptList?.length > 0) {
          newState.cptList = soap.pastSoapNoteDetails.cptList;
        }
        setValues(newState);
      }
    }

    if (soap && soap.pastSoapNoteDetails) {
      if (soap.fromIsMHx) {
        if (soap.pastSoapNoteDetails.prescriptions) {
          setPrescriptions(soap.pastSoapNoteDetails.prescriptions);
        }
        if (
          (soap.pastSoapNoteDetails.labOrders?.length > 0) ||
          (soap.pastSoapNoteDetails.labOrders?.labOrder?.length > 0) ||
          (soap.pastSoapNoteDetails.labOrders?.labOrderDiagnosis?.length > 0)
        ) {
          let data = { labOrder: [], labOrderDiagnosis: [] };
          soap.pastSoapNoteDetails.labOrders.labOrder?.forEach(item => data.labOrder.push({ text: item.text }));
          soap.pastSoapNoteDetails.labOrders.labOrderDiagnosis?.forEach(item =>
            data.labOrderDiagnosis.push({ code: item.code, description: item.description })
          );
          setLabOrders(data);
        }
        if (
          (soap.pastSoapNoteDetails.imagingOrders?.length > 0) ||
          (soap.pastSoapNoteDetails.imagingOrders?.labOrderDiagnosis?.length > 0)
        ) {
          let imgdata = { labOrder: [], labOrderDiagnosis: [] };
          soap.pastSoapNoteDetails.imagingOrders.labOrder?.forEach(item => imgdata.labOrder.push({ text: item.text }));
          soap.pastSoapNoteDetails.imagingOrders.labOrderDiagnosis?.forEach(item =>
            imgdata.labOrderDiagnosis.push({ code: item.code, description: item.description })
          );
          setImagingOrders(imgdata);
        }
        if (
          (soap.pastSoapNoteDetails.physicianOrders?.length > 0) ||
          (soap.pastSoapNoteDetails.physicianOrders?.labOrderDiagnosis?.length > 0)
        ) {
          let phydata = { labOrder: [], labOrderDiagnosis: [] };
          soap.pastSoapNoteDetails.physicianOrders.labOrder?.forEach(item => phydata.labOrder.push({ text: item.text }));
          soap.pastSoapNoteDetails.physicianOrders.labOrderDiagnosis?.forEach(item =>
            phydata.labOrderDiagnosis.push({ code: item.code, description: item.description })
          );
          setPhysicianOrders(phydata);
        }
      }

      if (soap.pastSoapNoteDetails.prescriptions && (!isCopy || soap.isCopyNeglect)) {
        setPrescriptions(soap.pastSoapNoteDetails.prescriptions);
      }

      // Lab Orders
      const labSrc = soap.pastSoapNoteDetails.labOrders;
      if (
        ((labSrc?.length > 0) || (labSrc?.labOrderDiagnosis?.length > 0)) &&
        isEdit && (!isCopy || soap.isCopyNeglect)
      ) {
        let data = { labOrder: [], labOrderDiagnosis: [] };
        if (labSrc?.length > 0) {
          labSrc.forEach(item => data.labOrder.push({ id: item[0], text: item[1] }));
        } else {
          labSrc.labOrder?.forEach(item => data.labOrder.push({ id: item.id, text: item.text }));
        }
        labSrc.labOrderDiagnosis?.forEach(item =>
          data.labOrderDiagnosis.push({
            id: item[0] !== undefined ? item[0] : item.id,
            code: item[2] !== undefined ? item[2] : item.code,
            description: item[1] !== undefined ? item[1] : item.description
          })
        );
        setLabOrders(data);
      } else if (labSrc && (!isCopy || soap.isCopyNeglect) && !isEdit) {
        let data = { labOrder: [], labOrderDiagnosis: [] };
        labSrc.labOrder?.forEach(item => data.labOrder.push({ text: item.text }));
        labSrc.labOrderDiagnosis?.forEach(item =>
          data.labOrderDiagnosis.push({ code: item.code, description: item.description })
        );
        setLabOrders(data);
      }

      // Imaging Orders
      const imgSrc = soap.pastSoapNoteDetails.imagingOrders;
      if (
        ((imgSrc?.length > 0) || (imgSrc?.labOrderDiagnosis?.length > 0)) &&
        isEdit && (!isCopy || soap.isCopyNeglect)
      ) {
        let imgdata = { labOrder: [], labOrderDiagnosis: [] };
        if (imgSrc?.length > 0) {
          imgSrc.forEach(item => imgdata.labOrder.push({ id: item[0], text: item[1] }));
        } else {
          imgSrc.labOrder?.forEach(item => imgdata.labOrder.push({ id: item.id, text: item.text }));
        }
        imgSrc.labOrderDiagnosis?.forEach(item =>
          imgdata.labOrderDiagnosis.push({
            id: item[0] !== undefined ? item[0] : item.id,
            code: item[2] !== undefined ? item[2] : item.code,
            description: item[1] !== undefined ? item[1] : item.description
          })
        );
        setImagingOrders(imgdata);
      } else if (imgSrc && (!isCopy || soap.isCopyNeglect) && !isEdit) {
        let imgdata = { labOrder: [], labOrderDiagnosis: [] };
        imgSrc.labOrder?.forEach(item => imgdata.labOrder.push({ text: item.text }));
        imgSrc.labOrderDiagnosis?.forEach(item =>
          imgdata.labOrderDiagnosis.push({ code: item.code, description: item.description })
        );
        setImagingOrders(imgdata);
      }

      // Physician Orders
      const phySrc = soap.pastSoapNoteDetails.physicianOrders;
      if (
        ((phySrc?.length > 0) || (phySrc?.labOrderDiagnosis?.length > 0)) &&
        isEdit && (!isCopy || soap.isCopyNeglect)
      ) {
        let phydata = { labOrder: [], labOrderDiagnosis: [] };
        if (phySrc?.length > 0) {
          phySrc.forEach(item => phydata.labOrder.push({ id: item[0], text: item[1] }));
        } else {
          phySrc.labOrder?.forEach(item => phydata.labOrder.push({ id: item.id, text: item.text }));
        }
        phySrc.labOrderDiagnosis?.forEach(item =>
          phydata.labOrderDiagnosis.push({
            id: item[0] !== undefined ? item[0] : item.id,
            code: item[2] !== undefined ? item[2] : item.code,
            description: item[1] !== undefined ? item[1] : item.description
          })
        );
        setPhysicianOrders(phydata);
      } else if (phySrc && (!isCopy || soap.isCopyNeglect) && !isEdit) {
        let phydata = { labOrder: [], labOrderDiagnosis: [] };
        phySrc.labOrder?.forEach(item => phydata.labOrder.push({ text: item.text }));
        phySrc.labOrderDiagnosis?.forEach(item =>
          phydata.labOrderDiagnosis.push({ code: item.code, description: item.description })
        );
        setPhysicianOrders(phydata);
      }
    }
  };

  useEffect(() => {
    loadCopyEditSoapDetails(
      soap,
      soap && soap.isEdit ? soap.isEdit : false,
      soap && soap.isCopy ? soap.isCopy : false,
      soap && soap.isPast ? soap.isPast : false
    );
  }, [soap]);

  const saveTempData = async (isCopyNeglect = false) => {
    let vals = getValuesforCopy(isCopyNeglect);
    props.saveTempData(vals);
  };

  const handleFieldChange = (field, value, isTemplate = false) => {
    const newState = { ...values };
    if (field === 'providerId') {
      newState['providerId'] = value;
    } else if (field === 'height' || field === 'weight') {
      newState[field] = value;
      let bodymi = calculateBMI(
        field === 'height' ? value : values.height,
        field === 'weight' ? value : values.weight
      );
      newState['bodymassindex'] = bodymi ? Number(bodymi).toFixed(2) : '';
    } else if (field === 'diagnosisList' && newState[field] && newState[field].length > 0) {
      let newData = [...newState[field]];
      value && value.length > 0 && value.forEach(item => newData.push(item));
      newState[field] = newData;
      setDiagnosisList(newData);
    } else if (field === 'cptList' && newState[field] && newState[field].length > 0) {
      let newData = [...newState[field]];
      value && value.length > 0 && value.forEach(item => newData.push(item));
      newState[field] = newData;
      setCPTList(newData);
    } else if (field === 'subjective' && isTemplate) {
      const input = subjectiveRef.current;
      if (!input) return;
      const { selectionStart, selectionEnd } = input;
      const newValue = values.subjective.substring(0, selectionStart) + value + values.subjective.substring(selectionEnd);
      newState[field] = newValue || value;
    } else if (field === 'objective' && isTemplate) {
      const input = objectiveRef.current;
      if (!input) return;
      const { selectionStart, selectionEnd } = input;
      const newValue = values.objective.substring(0, selectionStart) + value + values.objective.substring(selectionEnd);
      newState[field] = newValue || value;
    } else if (field === 'assessmentplan' && isTemplate) {
      const input = assessmentRef.current;
      if (!input) return;
      const { selectionStart, selectionEnd } = input;
      const newValue = values.assessmentplan.substring(0, selectionStart) + value + values.assessmentplan.substring(selectionEnd);
      newState[field] = newValue || value;
    } else {
      newState[field] = value;
    }
    newState.touched[field] = true;
    const errors = validate(newState, schema);
    newState.errors = errors || {};
    newState.isValid = errors ? false : true;
    setValues(newState);
  };

  // ── VS Hx ────────────────────────────────────────────────────────────────
  const [openVsHx, setOpenVsHx] = useState(false);
  const handleOpenVsHx = () => setOpenVsHx(true);
  const handleCloseVsHx = () => setOpenVsHx(false);

  const handleCopyVsHx = item => {
    let copyVals = {
      ...values,
      bloodpressure: item.bloodpressure,
      temperature: item.temperature,
      heartrate: item.heartrate,
      respiratoryrate: item.respiratoryrate,
      weight: item.weight,
      height: item.height,
      bodymassindex: Number(item.bodymassindex).toFixed(2)
    };
    setValues(copyVals);
    let vals = getValuesforCopy(isCopy ? true : false, '', '', '', '', '', '', copyVals);
    props.saveTempData(vals);
  };

  const calculateBMI = (heightVal, weightVal) => {
    let bmi;
    const footToInches = 12;
    const imperialBMIFactor = 703;
    const heightPattern = new RegExp('^([0-9]+)\'?[ ]?([0-9]{1,2})?(["])?|$');
    const weightPattern = new RegExp('^([0-9]+)[ ]*([lbs])?|$');
    const matchHeight = heightPattern.exec(heightVal);
    const matchWeight = weightPattern.exec(weightVal);
    if (heightPattern.test(heightVal) && weightPattern.test(weightVal)) {
      let inches = parseInt(matchHeight[2] ? matchHeight[2] : '0', 10);
      let height = parseInt(matchHeight[1], 10) * footToInches + inches;
      let heightSquared = Math.pow(height, 2);
      bmi = (parseInt(matchWeight[1], 10) * imperialBMIFactor) / heightSquared;
    }
    return bmi;
  };

  const renderVsHx = () => (
    <Modal
      aria-describedby="simple-modal-description"
      aria-labelledby="simple-modal-title"
      disableBackdropClick
      onClose={handleCloseVsHx}
      open={openVsHx}
    >
      <Box className={classes.modalWrap} style={{ width: 750 }}>
        <Box className={classes.modalHead}>
          <Typography variant="h2">VS Hx</Typography>
          <IconButton aria-label="close" className={classes.modalClose} onClick={handleCloseVsHx}>
            <CloseOutlined />
          </IconButton>
        </Box>
        <Box style={{ height: 480, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <VSHx handleCopyVsHx={handleCopyVsHx} onCancel={handleCloseVsHx} patientId={patientId} saveTempData={saveTempData} />
        </Box>
      </Box>
    </Modal>
  );

  // ── Rx ────────────────────────────────────────────────────────────────────
  const [openRx, setOpenRx] = useState(false);
  const [prescriptions, setPrescriptions] = useState(props.unSavedPrescription || {});
  const [medicationComment, setMedicationComment] = useState(props.unSavedMedicationComment || '');

  useEffect(() => {
    if (props.prescriptions && props.prescriptions.length > 0) {
      setPrescriptions(props.prescriptions);
    }
  }, [props.prescriptions]);

  useEffect(() => {
    if (props.medicationComment && props.medicationComment.length > 0) {
      setMedicationComment(props.medicationComment);
    }
  }, [props.medicationComment]);

  const handleOpenRx = () => setOpenRx({ open: true, isNewRx: true });
  const handleCloseRx = () => setOpenRx(false);

  const handlePrescription = prescriptions => {
    setPrescriptions(prescriptions);
    let vals = getValuesforCopy(false, prescriptions);
    props.saveTempData(vals);
  };

  const handleMedicationComment = medComment => {
    setMedicationComment(medComment);
    setValues({ ...values, medicationComment: medComment });
  };

  const handleESignPrint = (patientId, prescriptions) => {
    setOpenRx(false);
    props.downloadPDFRx(ehrID, patientId, userID, prescriptions, medicationComment).then(res => {
      if (res.status) {
        let file = new Blob([res.result.data], { type: 'application/pdf' });
        let fileURL = URL.createObjectURL(file);
        window.open(fileURL, `_blank_${Date.now()}`);
      }
    });
  };

  const editMedication = () => setOpenRx({ open: true, isNewRx: false });

  const renderRx = () => (
    <Modal
      aria-describedby="simple-modal-description"
      aria-labelledby="simple-modal-title"
      disableBackdropClick
      onClose={handleCloseRx}
      open={openRx}
    >
      <Box className={classes.modalWrap} style={{ width: '90%', maxWidth: 1200 }}>
        <Box className={classes.modalHead}>
          <Typography variant="h2">Prescription</Typography>
          <IconButton aria-label="close" className={classes.modalClose} onClick={handleCloseRx}>
            <CloseOutlined />
          </IconButton>
        </Box>
        {patientInfo && (
          <ApRx
            handleESignPrint={handleESignPrint}
            handleMedicationComment={handleMedicationComment}
            handlePrescription={handlePrescription}
            isNewRx={openRx.isNewRx}
            medicationComment={medicationComment}
            onCancel={handleCloseRx}
            patientId={patientId}
            patientInfo={patientInfo}
            prescriptions={prescriptions}
          />
        )}
      </Box>
    </Modal>
  );

  // ── Lab / Imaging / Physician Orders ─────────────────────────────────────
  const [openOrders, setOpenOrders] = useState(false);
  const [labOrders, setLabOrders] = useState({});
  const [imagingOrders, setImagingOrders] = useState({});
  const [physicianOrders, setPhysicianOrders] = useState({});

  useEffect(() => {
    if (values && values.labOrder) setLabOrders(values.labOrder);
    if (values && values.imagingOrder) setImagingOrders(values.imagingOrder);
    if (values && values.physicianOrder) setPhysicianOrders(values.physicianOrder);
    if (values && values.diagnosisList && values.diagnosisList.length > 0) setDiagnosisList(values.diagnosisList);
    if (values && values.cptList && values.cptList.length > 0) setCPTList(values.cptList);
  }, [values]);

  const handleOpenOrders = typeOrder => {
    setOpenOrders({ open: true, typeOrder: typeOrder, isNewLabOrder: true });
  };
  const handleCloseOrders = () => setOpenOrders(false);
  const editLabOrder = typeOrder => setOpenOrders({ open: true, typeOrder: typeOrder, isNewLabOrder: false });

  const handleLabESignPrint = async (patientId, labOrders) => {
    let data = [];
    labOrders?.labOrder?.forEach(item => data.push({ labDetails: item.text }));
    labOrders?.labOrderDiagnosis?.forEach(item => data.push({ code: item.code, diagnosisDetails: item.description }));
    await props.downloadPDFLab(ehrID, patientId, userID, data).then(res => {
      if (res.status) {
        let file = new Blob([res.result.data], { type: 'application/pdf' });
        window.open(URL.createObjectURL(file), `_blank_${Date.now()}`);
      }
    });
  };

  const handleLabESignPrintImage = async (patientId, imagingOrders) => {
    let data = [];
    imagingOrders?.labOrder?.forEach(item => data.push({ imagingDetails: item.text }));
    imagingOrders?.labOrderDiagnosis?.forEach(item => data.push({ code: item.code, diagnosisDetails: item.description }));
    await props.downloadPDFImage(ehrID, patientId, userID, data).then(res => {
      if (res.status) {
        let file = new Blob([res.result.data], { type: 'application/pdf' });
        window.open(URL.createObjectURL(file), `_blank_${Date.now()}`);
      }
    });
  };

  const handleLabESignPrintPhysician = async (patientId, physicianOrders) => {
    let data = [];
    physicianOrders?.labOrder?.forEach(item => data.push({ physicianDetails: item.text }));
    physicianOrders?.labOrderDiagnosis?.forEach(item => data.push({ code: item.code, diagnosisDetails: item.description }));
    await props.downloadPDFPhysician(ehrID, patientId, userID, data).then(res => {
      if (res.status) {
        let file = new Blob([res.result.data], { type: 'application/pdf' });
        window.open(URL.createObjectURL(file), `_blank_${Date.now()}`);
      }
    });
  };

  const renderOrders = () => {
    if (!openOrders || !openOrders.open) return null;
    return (
      <Modal
        aria-describedby="simple-modal-description"
        aria-labelledby="simple-modal-title"
        disableBackdropClick
        onClose={handleCloseOrders}
        open={openOrders.open}
      >
        <Box className={classes.modalWrap} style={{ width: '90%', maxWidth: 1200 }}>
          <Box className={classes.modalHead}>
            <Typography variant="h2">
              {openOrders.typeOrder === 1 ? 'Lab' : openOrders.typeOrder === 2 ? 'Imaging' : 'Physician'} Orders
            </Typography>
            <IconButton aria-label="close" className={classes.modalClose} onClick={handleCloseOrders}>
              <CloseOutlined />
            </IconButton>
          </Box>
          <ApLabOrders
            handleFieldChange={handleFieldChange}
            handleLabESignPrint={
              openOrders.typeOrder === 1 ? handleLabESignPrint :
                openOrders.typeOrder === 2 ? handleLabESignPrintImage :
                  openOrders.typeOrder === 3 ? handleLabESignPrintPhysician : ''
            }
            isNewLabOrder={openOrders.isNewLabOrder}
            labOrders={
              openOrders.typeOrder === 1 ? labOrders :
                openOrders.typeOrder === 2 ? imagingOrders :
                  openOrders.typeOrder === 3 ? physicianOrders : ''
            }
            onCancel={handleCloseOrders}
            patientId={patientId}
            patientInfo={patientInfo}
            typeOrder={openOrders.typeOrder}
            getValuesforCopy={getValuesforCopy}
          />
        </Box>
      </Modal>
    );
  };

  // ── Success ───────────────────────────────────────────────────────────────
  const handleCloseSuccess = () => setSuccMsg(false);
  const renderSuccess = () => (
    <MessageBox
      message={isEdit ? 'Soap updated successfully' : 'SOAP created successfully'}
      onClose={handleCloseSuccess}
      open={succMsg}
      variant="success"
    />
  );

  // ── Templates ─────────────────────────────────────────────────────────────
  const [opentemplates, setOpenTemplates] = useState(false);
  const handleOpenTemplates = type => setOpenTemplates({ open: true, type: type });
  const handleCloseTemplates = () => setOpenTemplates(false);

  const renderTemplates = () => (
    <Modal
      aria-describedby="simple-modal-description"
      aria-labelledby="simple-modal-title"
      disableBackdropClick
      onClose={handleCloseTemplates}
      open={opentemplates.open}
    >
      <Box className={classes.modalWrap} style={{ width: '90%', maxWidth: 1200 }}>
        <Box className={classes.modalHead}>
          <Typography variant="h2">
            {opentemplates.type === 1 ? 'Subjective' : opentemplates.type === 2 ? 'Objective' : 'Assessment Plan'} Templates
          </Typography>
          <IconButton aria-label="close" className={classes.modalClose} onClick={handleCloseTemplates}>
            <CloseOutlined />
          </IconButton>
        </Box>
        <Box style={{ minHeight: 400, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <MyTemplates handleFieldChange={handleFieldChange} onCancel={handleCloseTemplates} type={opentemplates.type} />
        </Box>
      </Box>
    </Modal>
  );

  // ── CPT / ICD ─────────────────────────────────────────────────────────────
  const [openCPTICD, setOpenCPTICD] = useState(false);
  const [diagnosisList, setDiagnosisList] = useState([]);
  const [cptList, setCPTList] = useState([]);

  const handleOpenCPTICD = isCPT => { setIsCPT(isCPT); setOpenCPTICD(true); };
  const handleCloseCPTICD = () => setOpenCPTICD(false);

  const renderCPTICD = () => (
    <Modal
      aria-describedby="simple-modal-description"
      aria-labelledby="simple-modal-title"
      disableBackdropClick
      onClose={handleCloseCPTICD}
      open={openCPTICD}
    >
      <Box className={classes.modalWrap} style={{ width: '90%', maxWidth: 1200 }}>
        <Box className={classes.modalHead}>
          <Typography variant="h2">{isCPT ? 'CPT Database' : 'Diagnosis Database'}</Typography>
          <IconButton aria-label="close" className={classes.modalClose} onClick={handleCloseCPTICD}>
            <CloseOutlined />
          </IconButton>
        </Box>
        <CPT handleFieldChange={handleFieldChange} isCPT={isCPT} onCancel={handleCloseCPTICD} getValuesforCopy={getValuesforCopy} />
      </Box>
    </Modal>
  );

  // ── Delete SOAP ───────────────────────────────────────────────────────────
  const renderDeleteSuccess = () => (
    <MessageBox
      message="SOAP deleted successfully"
      onClose={handleCloseDeleteSuccess}
      open={succDeleteMsg}
      variant="success"
    />
  );

  const handleOpenDelete = () => setOpenDelete(true);

  const handleDeleteOK = async () => {
    setOpenDelete(false);
    if (values.id === undefined || values.id === 0) {
      clearList(false);
    } else {
      const res = await props.deleteSoap(ehrID, userID, patientId, editSoap.id);
      if (res) {
        clearList();
        resetValue();
        setSuccDeleteMsg(true);
      }
    }
  };

  const handleDeleteCancel = () => setOpenDelete(false);
  const handleCloseDeleteSuccess = () => setSuccDeleteMsg(false);

  const renderDeleteDialogue = () => (
    <Dialog
      aria-labelledby="confirmation-dialog-title"
      disableBackdropClick
      disableEscapeKeyDown
      maxWidth="xs"
      open={opendelete}
    >
      <DialogTitle id="confirmation-dialog-title">Delete Soap Note</DialogTitle>
      <DialogContent>
        <DialogContentText>Do you want to confirm and delete the soap note?</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={handleDeleteOK}>OK</Button>
        <Button color="primary" onClick={handleDeleteCancel}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );

  // ── Delete CPT / ICD ──────────────────────────────────────────────────────
  const [openDeleteCPTICD, setOpenDeleteCPTICD] = useState({ showPopUp: false, idx: 0, id: 0 });
  const [succDeleteMsgCPTICD, setSuccDeleteMsgCPTICD] = useState({ succ: false, succMsg: '' });

  const renderDeleteCPTICDSuccess = () => (
    <MessageBox
      message={succDeleteMsgCPTICD.succMsg}
      onClose={handleCloseDeleteCPTICDSuccess}
      open={succDeleteMsgCPTICD.succ}
      variant="success"
    />
  );

  const handleOpenDeleteCPTICD = (isCPT, idx, id) => {
    setIsCPT(isCPT);
    setOpenDeleteCPTICD({ showPopUp: true, idx, id });
  };

  const handleDeleteCPTICDCancel = () => setOpenDeleteCPTICD({ showPopUp: false });

  const handleCloseDeleteCPTICDSuccess = () => {
    setOpenDeleteCPTICD({ showPopUp: false });
    setSuccDeleteMsgCPTICD({ succ: false });
  };

  const handleDeleteDiagnosisList = async () => {
    setOpenDeleteCPTICD({ showPopUp: false });
    let idx = openDeleteCPTICD.idx;
    let newDia = [...diagnosisList];
    if (newDia[idx] && newDia[idx].id) await props.deleteDiagnosis(patientId, newDia[idx].id);
    newDia.splice(idx, 1);
    setDiagnosisList(newDia);
    let newSta = { ...values, diagnosisList: newDia };
    setValues(newSta);
    await props.handleCopy(newSta.diagnosisList, cptList, true, false);
    setSuccDeleteMsgCPTICD({ succ: true, succMsg: 'Diagnosis deleted.' });
  };

  const handleDeleteCPTList = async () => {
    setOpenDeleteCPTICD({ showPopUp: false });
    let idx = openDeleteCPTICD.idx;
    let newCPT = [...cptList];
    if (newCPT[idx] && newCPT[idx].id) await props.deleteDiagnosis(patientId, newCPT[idx].id);
    newCPT.splice(idx, 1);
    setCPTList(newCPT);
    let newSta = { ...values, cptList: newCPT };
    setValues(newSta);
    await props.handleCopy(diagnosisList, newSta.cptList, false, true);
    setSuccDeleteMsgCPTICD({ succ: true, succMsg: 'CPT deleted.' });
  };

  const renderDeleteCPTICDDialogue = () => (
    <Dialog
      aria-labelledby="confirmation-dialog-title"
      disableBackdropClick
      disableEscapeKeyDown
      maxWidth="xs"
      open={openDeleteCPTICD.showPopUp}
    >
      <DialogTitle id="confirmation-dialog-title">Delete {isCPT ? 'CPT' : 'ICD'}</DialogTitle>
      <DialogContent>
        <DialogContentText>Do you want to confirm and delete the {isCPT ? 'CPT' : 'ICD'}?</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={() => isCPT ? handleDeleteCPTList() : handleDeleteDiagnosisList()}>OK</Button>
        <Button color="primary" onClick={handleDeleteCPTICDCancel}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );

  // ── Helpers ───────────────────────────────────────────────────────────────
  const clearList = async (clearLocation = true) => {
    setValues(initialUserState);
    setPrescriptions([]);
    setLabOrders([]);
    setImagingOrders([]);
    setPhysicianOrders([]);
    setDiagnosisList([]);
    setCPTList([]);
    setEditSoap([]);
    if (clearLocation) {
      await props.clearLocationandEditChartView();
    }
  };

  const getValuesforCopy = (
    isCopyNeglect, newPrescriptions, newlabOrders,
    newimagingOrders, newphysicianOrders, newcptList, newdiagnosisList, copyValsforVsHx
  ) => ({
    soap: copyValsforVsHx ? copyValsforVsHx : values,
    pastSoapNoteDetails: {
      prescriptions: newPrescriptions ? newPrescriptions : prescriptions,
      labOrders: newlabOrders ? newlabOrders : labOrders,
      imagingOrders: newimagingOrders ? newimagingOrders : imagingOrders,
      physicianOrders: newphysicianOrders ? newphysicianOrders : physicianOrders,
      diagnosisList: newdiagnosisList && newdiagnosisList.length > 0
        ? [...newdiagnosisList, ...diagnosisList] : diagnosisList,
      cptList: newcptList && newcptList.length > 0
        ? [...newcptList, ...cptList] : cptList
    },
    isCopy, isEdit, isPast, editSoap,
    isCopyNeglect: isCopyNeglect || values.isCopyNeglect,
    fromIsMHx: !!(newPrescriptions || newlabOrders || newimagingOrders || newphysicianOrders || newcptList || newdiagnosisList)
  });

  const hasSoapContent = values && (values.subjective !== '' || values.objective !== '' || values.assessmentplan !== '');

  const btnSaveSOAPPatient = async () => {
    if (!hasSoapContent) return;
    setSuccMsg(true);
    let newstate;
    if (isEdit) {
      newstate = {
        ...editSoap, visitdate: values.visitdate, soapnote: {
          medicationComment, id: values.id,
          bloodpressure: values.bloodpressure, temperature: values.temperature,
          heartrate: values.heartrate, respiratoryrate: values.respiratoryrate,
          weight: values.weight, bodymassindex: values.bodymassindex, height: values.height,
          subjective: values.subjective, objective: values.objective,
          assessmentplan: values.assessmentplan, visitdate: values.visitdate, signed: false
        }
      };
    } else {
      newstate = {
        soapnote: {
          bloodpressure: values.bloodpressure, temperature: values.temperature,
          heartrate: values.heartrate, respiratoryrate: values.respiratoryrate,
          weight: values.weight, bodymassindex: values.bodymassindex, height: values.height,
          subjective: values.subjective, objective: values.objective,
          assessmentplan: values.assessmentplan, visitdate: values.visitdate,
          signed: false, medicationComment
        }, visitdate: values.visitdate, signed: false
      };
    }
    const res = await props.saveSOAPPatientData(ehrID, values.providerId, patientId, false, newstate);
    if (res.status) {
      await _saveRelatedData(res.soapId);
      getPastSoapNoteById(patientId, 0);
      await resetValue();
      clearList();
      setIsEdit(false); setIsCopy(false); setIsPast(false);
    }
  };

  const btnSaveSOAPPatientSign = async () => {
    if (!hasSoapContent && !(values.signed && values.addendum)) return;
    setSuccMsg(true);
    let newstate;
    if (isEdit) {
      let addendaVal = values.addendum ? [{ addendum: values.addendum, signature: { firstname: user.firstname, lastname: user.lastname, title: user.title } }] : [];
      newstate = {
        ...editSoap, visitdate: values.visitdate, signed: true, soapnote: {
          id: values.id, bloodpressure: values.bloodpressure, temperature: values.temperature,
          heartrate: values.heartrate, respiratoryrate: values.respiratoryrate,
          weight: values.weight, bodymassindex: values.bodymassindex, height: values.height,
          subjective: values.subjective, objective: values.objective,
          assessmentplan: values.assessmentplan, medicationComment, visitdate: values.visitdate,
          signed: true, signedby: values.providerId, signedbydate: new Date(), addenda: addendaVal
        }
      };
    } else {
      newstate = {
        soapnote: {
          bloodpressure: values.bloodpressure, temperature: values.temperature,
          heartrate: values.heartrate, respiratoryrate: values.respiratoryrate,
          weight: values.weight, bodymassindex: values.bodymassindex, height: values.height,
          subjective: values.subjective, objective: values.objective,
          assessmentplan: values.assessmentplan, visitdate: values.visitdate,
          signed: true, signedby: values.providerId, medicationComment, signedbydate: new Date()
        }, visitdate: values.visitdate, signed: true
      };
    }
    const res = await props.saveSOAPPatientData(ehrID, values.providerId, patientId, true, newstate, true);
    if (res.status) {
      // const soapNoteId = !isEdit ? res.soapId : res.soapId?.soapnote?.id;
      await _saveRelatedData(res.soapId?.soapnote?.id);
      getPastSoapNoteById(patientId, 0);
      await resetValue();
      clearList();
      setIsEdit(false); setIsCopy(false); setIsPast(false);
    }
  };

  // Shared helper to save prescriptions, orders, diagnosis after SOAP save
  const _saveRelatedData = async (soapId, isSigned = false) => {
    if (prescriptions?.length > 0 && prescriptions.filter(c => !c.hasOwnProperty('id'))) {
      await props.savePrescription(ehrID, patientId, values.providerId, soapId, prescriptions);
    }
    const buildOrderData = (orders, detailKey) => {
      let data = [];
      orders?.labOrder?.filter(c => !c.hasOwnProperty('id') || c.id === undefined).forEach(item => data.push({ [detailKey]: item.text }));
      orders?.labOrderDiagnosis?.filter(c => !c.hasOwnProperty('id') || c.id === undefined).forEach(item => data.push({ code: item.code, diagnosisDetails: item.description }));
      return data;
    };
    const labData = buildOrderData(labOrders, 'labDetails');
    if (labData.length > 0) await props.savelabOrders(ehrID, patientId, values.providerId, soapId, labData);
    const imgData = buildOrderData(imagingOrders, 'imagingDetails');
    if (imgData.length > 0) await props.saveImagingOrders(ehrID, patientId, values.providerId, soapId, imgData);
    const phyData = buildOrderData(physicianOrders, 'physicianDetails');
    if (phyData.length > 0) await props.savePhysicianOrders(ehrID, patientId, values.providerId, soapId, phyData);
    let diagnosis = [
      ...(diagnosisList?.filter(c => !c.hasOwnProperty('id')) || []),
      ...(cptList?.filter(c => !c.hasOwnProperty('id')) || [])
    ];
    if (diagnosis.length > 0) await props.saveDiagnosis(ehrID, patientId, values.providerId, soapId, diagnosis);
  };

  // ─── Order Section Header ─────────────────────────────────────────────────
  const OrderSectionHeader = ({ label, onEdit }) => (
    <span style={{
      background: 'linear-gradient(90deg, #1a73e8, #0d5fce)',
      padding: '6px 12px',
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      borderRadius: '8px 8px 0 0'
    }}>
      <Typography style={{ color: '#fff', fontWeight: 700, fontSize: 13, letterSpacing: '0.04em' }}>
        {label}
      </Typography>
      <IconButton onClick={onEdit} size="small" style={{ color: '#fff', padding: 4 }}>
        <EditOutlined style={{ fontSize: 16 }} />
      </IconButton>
    </span>
  );

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <>
      <Grid item xs={!isPast ? 6 : 12}>
        <PerfectScrollbar style={{ height: '100vh', paddingRight: 10 }}>
          <Box className={classes.soapEntryWrap}>

            {/* Visit Date */}
            <Box className={clsx(classes.soapEntryItem, classes.soapEntryTitle)}>
              <div style={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap', justifyContent: 'space-between' }}>
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  <Typography>Patient Visit Date: &nbsp;</Typography>
                  <div className={classes.datePickerWrap}>
                    <DatePicker
                      customInput={
                        <TextField
                          className={classes.textField}
                          fullWidth
                          InputProps={{ endAdornment: (<InputAdornment><CalendarToday style={{ height: 16, width: 16 }} /></InputAdornment>) }}
                          label="Visit date"
                          margin="dense"
                          variant="outlined"
                        />
                      }
                      format="MM/DD/YYYY"
                      onBlur={saveTempData}
                      onChange={event => handleFieldChange('visitdate', event)}
                      onFocus={e => (e.target.placeholder = '')}
                      placeholderText="Patient Visit Date"
                      selected={values && values.visitdate && new Date(values.visitdate)}
                      showMonthDropdown="bool"
                      showYearDropdown="bool"
                    />
                  </div>
                </span>
                {isPast && (
                  <IconButton onClick={() => props.setPast()} size="small">
                    <FirstPage />
                  </IconButton>
                )}
              </div>
            </Box>

            {/* Vital Signs */}
            <Box className={classes.soapEntryItem}>
              <div className={classes.vitalHead}>
                <Typography>Vital Signs: &nbsp;</Typography>
                {values && !values.signed && (
                  <Button variant="outlined" color="primary" onClick={handleOpenVsHx}>
                    VS Hx
                  </Button>
                )}
              </div>
              <div className={classes.vitalBody}>
                {[
                  { label: 'BP', field: 'bloodpressure' },
                  { label: 'Temp', field: 'temperature' },
                  { label: 'HR', field: 'heartrate' },
                  { label: 'RR', field: 'respiratoryrate' },
                  { label: 'WT (lb)', field: 'weight' },
                  { label: 'HT (ft)', field: 'height' },
                  { label: 'BMI', field: 'bodymassindex', readOnly: false, formatVal: v => v ? Number(v).toFixed(2) : '' },
                ].map(({ label, field, formatVal }) => (
                  <TextField
                    key={field}
                    className={classes.TextVitals}
                    InputLabelProps={{ classes: { root: classes.labelRoot, focused: classes.labelFocused } }}
                    label={label}
                    onBlur={saveTempData}
                    onChange={event => handleFieldChange(field, event.target.value)}
                    value={formatVal ? formatVal(values[field]) : (values && values[field] ? values[field] : '')}
                    variant="outlined"
                  />
                ))}
              </div>
            </Box>

            {/* Subjective */}
            <Box className={classes.soapEntryItem}>
              <div className={classes.soapEntryBtn}>
                {values && !values.signed && (
                  <Btn variant="template" onClick={() => handleOpenTemplates(1)}>
                    <DescriptionOutlined style={{ fontSize: 15 }} /> Subjective Template
                  </Btn>
                )}
              </div>
              <TextField
                inputRef={subjectiveRef}
                className={classes.textField}
                InputLabelProps={{ shrink: true, classes: { root: classes.labelRoot, focused: classes.labelFocused } }}
                InputProps={{ classes: { input: classes.textfiledSize }, readOnly: values && values.signed && isEdit }}
                label="Subjective"
                multiline
                onBlur={saveTempData}
                onChange={event => handleFieldChange('subjective', event.target.value)}
                value={values && values.subjective ? values.subjective : ''}
                variant="outlined"
              />
            </Box>

            {/* Objective */}
            <Box className={classes.soapEntryItem}>
              <div className={classes.soapEntryBtn}>
                {values && !values.signed && (
                  <Btn variant="template" onClick={() => handleOpenTemplates(2)}>
                    <DescriptionOutlined style={{ fontSize: 15 }} /> Objective Template
                  </Btn>
                )}
              </div>
              <TextField
                inputRef={objectiveRef}
                className={classes.textField}
                InputLabelProps={{ shrink: true, classes: { root: classes.labelRoot, focused: classes.labelFocused } }}
                InputProps={{ classes: { input: classes.textfiledSize }, readOnly: values && values.signed && isEdit }}
                label="Objective"
                multiline
                onBlur={saveTempData}
                onChange={event => handleFieldChange('objective', event.target.value)}
                value={values && values.objective ? values.objective : ''}
                variant="outlined"
              />
            </Box>

            {/* Orders & Rx — non-staff only */}
            {role !== Roles.staff && (
              <Box className={classes.soapEntryItem}>
                {values && !values.signed && (
                  <div className={clsx(classes.soapEntryBtn3, classes.soapEntryBtn)} style={{ display: 'flex', flexWrap: 'wrap', gap: 2, marginBottom: 5, }}>
                    <Button variant="outlined" color='primary' onClick={handleOpenRx}>
                      Rx
                    </Button>
                    <Button variant="outlined" color='primary' onClick={() => handleOpenOrders(1)}>
                      Lab Orders
                    </Button>
                    <Button variant="outlined" color='primary' onClick={() => handleOpenOrders(2)}>
                      Imaging Orders
                    </Button>
                    <Button variant="outlined" color='primary' onClick={() => handleOpenOrders(3)}>
                      Physician Orders
                    </Button>
                  </div>
                )}

                {/* Prescriptions table */}
                {prescriptions && prescriptions.length > 0 && (
                  <Box className={classes.soapEntryItem}>
                    <OrderSectionHeader label="Rx — Prescriptions" onEdit={editMedication} />
                    <Table className={classes.templateTable} style={{ border: '1px solid #c5d8fc', borderCollapse: 'initial', }}>
                      <TableHead>
                        <TableRow>
                          <TableCell width="25%">Medication</TableCell>
                          <TableCell>Dose</TableCell>
                          <TableCell width="25%">Direction</TableCell>
                          <TableCell>Quantity</TableCell>
                          <TableCell>Refill</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {prescriptions.map((item, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{item.name || ''}</TableCell>
                            <TableCell>{item.dose || ''}</TableCell>
                            <TableCell>{item.directions || ''}</TableCell>
                            <TableCell>{item.quantity || ''}</TableCell>
                            <TableCell>{item.refill || ''}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow>
                          <TableCell colSpan={5}>
                            {medicationComment ? medicationComment.split('\n').map((line, k) => <span key={k}>{line}<br /></span>) : ''}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </Box>
                )}

                {/* Lab Orders table */}
                {labOrders && ((labOrders.labOrder?.length > 0) || (labOrders.labOrderDiagnosis?.length > 0)) && (
                  <Box className={classes.soapEntryItem}>
                    <OrderSectionHeader label="Lab Orders" onEdit={() => editLabOrder(1)} />
                    <Table className={classes.templateTable} style={{ border: '1px solid #b7dfc5', borderCollapse: 'initial' }}>
                      <TableBody>
                        {labOrders.labOrder?.map((item, idx) => (
                          <TableRow key={idx}><TableCell>{item.text || ''}</TableCell></TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    <Table className={classes.templateTable} style={{ border: '1px solid #b7dfc5', borderCollapse: 'initial', marginTop: -1 }}>
                      <TableHead><TableRow><TableCell colSpan={2}>Diagnosis Details</TableCell></TableRow></TableHead>
                      <TableBody>
                        <TableRow><TableCell>Code</TableCell><TableCell>Description</TableCell></TableRow>
                        {labOrders.labOrderDiagnosis?.map((item, idx) => (
                          <TableRow key={idx}><TableCell>{item.code || ''}</TableCell><TableCell>{item.description || ''}</TableCell></TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}

                {/* Imaging Orders table */}
                {imagingOrders && ((imagingOrders.labOrder?.length > 0) || (imagingOrders.labOrderDiagnosis?.length > 0)) && (
                  <Box className={classes.soapEntryItem}>
                    <OrderSectionHeader label="Imaging Orders" onEdit={() => editLabOrder(2)} />
                    <Table className={classes.templateTable} style={{ border: '1px solid #f5cfa0', borderCollapse: 'initial' }}>
                      <TableBody>
                        {imagingOrders.labOrder?.map((item, idx) => (
                          <TableRow key={idx}><TableCell>{item.text || ''}</TableCell></TableRow>
                        ))}
                        {imagingOrders.labOrdertext && <TableRow><TableCell>{imagingOrders.labOrdertext}</TableCell></TableRow>}
                      </TableBody>
                    </Table>
                    <Table className={classes.templateTable} style={{ border: '1px solid #f5cfa0', borderCollapse: 'initial', marginTop: -1 }}>
                      <TableHead><TableRow><TableCell colSpan={2}>Diagnosis Details</TableCell></TableRow></TableHead>
                      <TableBody>
                        <TableRow><TableCell>Code</TableCell><TableCell>Description</TableCell></TableRow>
                        {imagingOrders.labOrderDiagnosis?.map((item, idx) => (
                          <TableRow key={idx}><TableCell>{item.code || ''}</TableCell><TableCell>{item.description || ''}</TableCell></TableRow>
                        ))}
                        {imagingOrders.diaOrdertext && <TableRow><TableCell>{imagingOrders.diaOrdertext}</TableCell></TableRow>}
                      </TableBody>
                    </Table>
                  </Box>
                )}

                {/* Physician Orders table */}
                {physicianOrders && ((physicianOrders.labOrder?.length > 0) || (physicianOrders.labOrderDiagnosis?.length > 0)) && (
                  <Box className={classes.soapEntryItem}>
                    <OrderSectionHeader label="Physician Orders" onEdit={() => editLabOrder(3)} />
                    <Table className={classes.templateTable} style={{ border: '1px solid #f5c2bf', borderCollapse: 'initial' }}>
                      <TableBody>
                        {physicianOrders.labOrder?.map((item, idx) => (
                          <TableRow key={idx}><TableCell>{item.text || ''}</TableCell></TableRow>
                        ))}
                        {physicianOrders.labOrdertext && <TableRow><TableCell>{physicianOrders.labOrdertext}</TableCell></TableRow>}
                      </TableBody>
                    </Table>
                    <Table className={classes.templateTable} style={{ border: '1px solid #f5c2bf', borderCollapse: 'initial', marginTop: -1 }}>
                      <TableHead><TableRow><TableCell colSpan={2}>Diagnosis Details</TableCell></TableRow></TableHead>
                      <TableBody>
                        <TableRow><TableCell>Code</TableCell><TableCell>Description</TableCell></TableRow>
                        {physicianOrders.labOrderDiagnosis?.map((item, idx) => (
                          <TableRow key={idx}><TableCell>{item.code || ''}</TableCell><TableCell>{item.description || ''}</TableCell></TableRow>
                        ))}
                        {physicianOrders.diaOrdertext && <TableRow><TableCell>{physicianOrders.diaOrdertext}</TableCell></TableRow>}
                      </TableBody>
                    </Table>
                  </Box>
                )}
              </Box>
            )}

            {/* Assessment Plan — non-staff only */}
            {role !== Roles.staff && (
              <Box className={classes.soapEntryItem}>
                <div className={classes.soapEntryBtn}>
                  {values && !values.signed && (
                    <Btn variant="template" onClick={() => handleOpenTemplates(3)}>
                      <DescriptionOutlined style={{ fontSize: 15 }} /> Assessment Plan Template
                    </Btn>
                  )}
                </div>
                <TextField
                  inputRef={assessmentRef}
                  className={classes.textField}
                  InputLabelProps={{ shrink: true, classes: { root: classes.labelRoot, focused: classes.labelFocused } }}
                  InputProps={{ classes: { input: classes.textfiledSize }, readOnly: values && values.signed && isEdit }}
                  label="Assessment Plan"
                  multiline
                  onBlur={saveTempData}
                  onChange={event => handleFieldChange('assessmentplan', event.target.value)}
                  value={values && values.assessmentplan ? values.assessmentplan : ''}
                  variant="outlined"
                />
              </Box>
            )}

            {/* CPT / ICD buttons */}
            <Box className={classes.soapEntryItem}>
              <div className={clsx(classes.soapEntryBtn2, classes.soapEntryBtn)} style={{ display: 'flex', gap: 8 }}>
                {values && !values.signed && (
                  <>
                    <Button variant="outlined" color="primary" onClick={() => handleOpenCPTICD(true)}>
                      CPT
                    </Button>
                    <Button variant="contained" color="primary" onClick={() => handleOpenCPTICD(false)}>
                      ICD 10 &amp; Diagnosis
                    </Button>
                  </>
                )}
              </div>
            </Box>

            {/* CPT table */}
            {cptList && cptList.length > 0 && (
              <Box className={classes.soapEntryItem}>
                <span style={{ background: 'linear-gradient(90deg, #4527a0, #6a1b9a)', padding: '6px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderRadius: '8px 8px 0 0' }}>
                  <Typography style={{ color: '#fff', fontWeight: 700, fontSize: 13 }}>CPT</Typography>
                </span>
                <Table className={classes.templateTable} style={{ border: '1px solid #c5b5e8', borderCollapse: 'initial' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell width="25%">Code</TableCell>
                      <TableCell colSpan={2}>Description</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cptList.map((item, idx) => (
                      <TableRow key={idx} id={'cpt' + idx}>
                        <TableCell>{item.code || ''}</TableCell>
                        <TableCell>{item.description || ''}</TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => handleOpenDeleteCPTICD(true, idx, item.id)}>
                            <DeleteOutlined style={{ fontSize: 16, color: '#e53935' }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}

            {/* Diagnosis table */}
            {diagnosisList && diagnosisList.length > 0 && (
              <Box className={classes.soapEntryItem}>
                <span style={{ background: 'linear-gradient(90deg, #0d47a1, #1565c0)', padding: '6px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderRadius: '8px 8px 0 0' }}>
                  <Typography style={{ color: '#fff', fontWeight: 700, fontSize: 13 }}>Diagnosis</Typography>
                </span>
                <Table className={classes.templateTable} style={{ border: '1px solid #90caf9', borderCollapse: 'initial' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell width="25%">Code</TableCell>
                      <TableCell colSpan={2}>Description</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {diagnosisList.map((item, idx) => (
                      <TableRow key={idx} id={'icd' + idx}>
                        <TableCell>{item.code || ''}</TableCell>
                        <TableCell>{item.description || ''}</TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => handleOpenDeleteCPTICD(false, idx, item.id)}>
                            <DeleteOutlined style={{ fontSize: 16, color: '#e53935' }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}

            {/* Addendum */}
            {values && values.signed && isEdit && (
              <Box className={classes.soapEntryItem}>
                <TextField
                  className={classes.textField}
                  InputLabelProps={{ shrink: true, classes: { root: classes.labelRoot, focused: classes.labelFocused } }}
                  InputProps={{ classes: { input: classes.textfiledSize } }}
                  label="Addendum"
                  multiline
                  onBlur={saveTempData}
                  onChange={event => handleFieldChange('addendum', event.target.value)}
                  value={values && values.addendum ? values.addendum : ''}
                  variant="outlined"
                />
              </Box>
            )}

            {/* Action bar — compact layout for 35% left panel */}
            <Box
              className={classes.soapEntryAction}
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                gap: 6,
                padding: '6px 0',
                width: '100%'
              }}
            >
              {values.signed && isEdit ? (
                /* Signed + edit state — Cancel only, full width */
                <Box style={{ display: 'flex', gap: 6, width: '100%' }}>
                  <Btn
                    variant="cancel"
                    onClick={() => setValues(initialUserState)}
                    style={{ flex: 1, justifyContent: 'center', fontSize: 12 }}
                  >
                    Cancel
                  </Btn>
                  {role !== Roles.staff && (
                    <Btn
                      variant="sign"
                      disabled={!values.addendum}
                      onClick={btnSaveSOAPPatientSign}
                      style={{ flex: 2, justifyContent: 'center', fontSize: 12 }}
                    >
                      <BorderColorOutlined style={{ fontSize: 14, marginRight: 3 }} />
                      SAVE &amp; E-SIGN
                    </Btn>
                  )}
                </Box>
              ) : (
                <>
                  {/* Row 1: Provider select — full width */}
                  <FormControl variant="standard" style={{ marginBottom: 10 }} className={classes.maxWidth}>
                    <Select
                      onChange={event => handleFieldChange('providerId', event.target.value)}
                      value={values && values.providerId}
                      style={{
                        width: '100%',
                        maxWidth: '50%!important',
                        height: 34,
                        fontSize: 12,
                        backgroundColor: '#f5f5f5',
                        borderRadius: 8,
                      }}
                      inputProps={{
                        style: {
                          padding: '0 8px',
                          height: 34,
                          display: 'flex',
                          alignItems: 'center',
                          fontSize: 12,
                          color: '#444',
                        }
                      }}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            borderRadius: 8,
                            marginTop: 4,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                            fontSize: 12,
                          }
                        }
                      }}
                    >
                      <MenuItem value="" style={{ fontSize: 12, color: '#888' }}>
                        Route to provider…
                      </MenuItem>
                      {pros && pros.map(item => (
                        <MenuItem key={item.value} value={item.value} style={{ fontSize: 12 }}>
                          {item.key}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Row 2: Delete + Save + E-Sign */}
                  <Box style={{ display: 'flex', gap: 6, width: '100%' }}>
                    <Btn
                      variant="physician"
                      onClick={handleOpenDelete}
                      style={{ flex: 1, justifyContent: 'center', fontSize: 12 }}
                    >
                      <DeleteOutlined style={{ fontSize: 14, marginRight: 3 }} />
                      DELETE
                    </Btn>
                    {role !== Roles.staff && (
                      <Btn
                        variant="save"
                        disabled={!hasSoapContent}
                        onClick={btnSaveSOAPPatient}
                        style={{ flex: 1, justifyContent: 'center', fontSize: 12 }}
                      >
                        <SaveOutlined style={{ fontSize: 14, marginRight: 3 }} />
                        SAVE
                      </Btn>
                    )}
                    {role !== Roles.staff && (
                      <Btn
                        variant="sign"
                        disabled={!hasSoapContent}
                        onClick={btnSaveSOAPPatientSign}
                        style={{ flex: 2, justifyContent: 'center', fontSize: 12 }}
                      >
                        <BorderColorOutlined style={{ fontSize: 14, marginRight: 3 }} />
                        SAVE &amp; E-SIGN
                      </Btn>
                    )}
                  </Box>
                </>
              )}
            </Box>

          </Box>

          {openVsHx && renderVsHx()}
          {opentemplates && renderTemplates()}
          {openRx && renderRx()}
          {openOrders && renderOrders()}
          {openCPTICD && renderCPTICD()}
          {succMsg && renderSuccess()}
          {opendelete && renderDeleteDialogue()}
          {succDeleteMsg && renderDeleteSuccess()}
          {succDeleteMsgCPTICD.succ && renderDeleteCPTICDSuccess()}
          {openDeleteCPTICD.showPopUp && renderDeleteCPTICDDialogue()}

        </PerfectScrollbar>
      </Grid>

      {!isPast && (
        <Grid item xs={6}>
          <SoapPast
            handleCopyPast={props.handleCopyPast}
            handleEditPast={props.handleEditPast}
            isPast={isPast}
            ref={props.childRef}
            setPast={props.setPast}
          />
        </Grid>
      )}
    </>
  );
});

SoapEntry.propTypes = {
  className: PropTypes.string
};

const mapDispatchToProps = {
  getPastSoapNoteById,
  downloadPDFRx,
  saveSOAPPatientData,
  savePrescription,
  savelabOrders,
  saveImagingOrders,
  savePhysicianOrders,
  saveDiagnosis,
  downloadPDFLab,
  downloadPDFImage,
  downloadPDFPhysician,
  deleteSoap,
  deleteDiagnosis,
  saveTempData,
  resetValue
};

const mapStateToProps = state => ({
  role: state.profile && state.profile.role,
  ehrID: state.profile && state.profile.ehrID,
  userID: state.profile && state.profile.userID,
  user: state.profile && state.profile,
  patientInfo: state.soap && state.soap.patientInfo,
  patientId: state.soap && state.soap.patientInfo && state.soap.patientInfo.id,
  providers: state.util.providers,
  soap: state.soap && state.soap.unsavedData
});

export default connect(mapStateToProps, mapDispatchToProps, null, { forwardRef: true })(withStyles(styles)(SoapEntry));